import React from 'react';
import Icon from '../icon/icon';
import * as s from './styled-delta-pill';

// Pill de variacao percentual vs periodo anterior. Mostra:
//   ▲ +8,3% (verde) quando crescimento e bom
//   ▼ -4,1% (vermelho) quando queda
//   = 0,0%  (cinza) quando estavel (variacao < 0.5%)
//   ⚠ sem base (cinza) quando previous=0 OU variacao > limiar absurdo
//
// Props:
// - current: numero do periodo atual
// - previous: numero do periodo anterior (mesma unidade)
// - invertSemantic: se true, queda e boa (ex.: consumo de energia EBA — menos
//     consumo = melhor). Default false (subida = boa).
// - small: variante menor pra inline em KPIs

// Acima deste limiar (%), a comparacao deixa de ser util — quase sempre
// indica que o periodo anterior tinha dado faltando/zerado (maquina
// nova, problema na coleta). Mostrar '+7784%' confunde mais do que
// ajuda. Limite escolhido empiricamente: 500% = 6x mudanca, raro num
// processo industrial estavel.
const ABSURD_DELTA_THRESHOLD_PCT = 500;

const DeltaPill = ({ current, previous, invertSemantic = false, small = false }) => {
  if (
    typeof current !== 'number' ||
    typeof previous !== 'number' ||
    !Number.isFinite(current) ||
    !Number.isFinite(previous)
  ) {
    return null;
  }

  // Caso 1: previous=0 e current!=0 — sem base de comparacao numerica.
  // Renderiza pill 'sem base' em vez de null pra deixar claro que houve
  // tentativa de comparacao (operador nao fica em duvida se nao apareceu).
  if (previous === 0) {
    if (current === 0) return null; // ambos zero — irrelevante
    return (
      <s.Pill
        variant="neutral"
        small={small}
        title="Período anterior sem dado pra comparar"
      >
        <s.Arrow>—</s.Arrow>
        sem base
      </s.Pill>
    );
  }

  const deltaAbs = current - previous;
  const deltaPct = (deltaAbs / Math.abs(previous)) * 100;
  const absPct = Math.abs(deltaPct);

  // Variacao < 0.5% e ruido — exibe como estavel.
  if (absPct < 0.5) {
    return (
      <s.Pill variant="neutral" small={small}>
        <s.Arrow>=</s.Arrow>
        0%
      </s.Pill>
    );
  }

  // Variacao absurda (> 500%): indica geralmente que o periodo anterior
  // teve dado muito sparse (maquina nova / falha de coleta), nao
  // crescimento real. Mostrar '+7784%' enganaria o operador.
  if (absPct > ABSURD_DELTA_THRESHOLD_PCT) {
    return (
      <s.Pill
        variant="neutral"
        small={small}
        title={`Variação muito grande (${deltaPct.toFixed(0)}%) — período anterior provavelmente sem dado suficiente`}
      >
        <s.Arrow as="span">
          <Icon name="alert-triangle" size={12} />
        </s.Arrow>
        sem base
      </s.Pill>
    );
  }

  const positive = deltaPct > 0;
  // Se queda e boa (invertSemantic), trocamos a variante de cor
  const isGood = invertSemantic ? !positive : positive;
  const variant = isGood ? 'good' : 'bad';

  return (
    <s.Pill variant={variant} small={small} title={`${deltaAbs.toFixed(2)} vs anterior`}>
      <s.Arrow>{positive ? '▲' : '▼'}</s.Arrow>
      {positive ? '+' : ''}
      {deltaPct.toFixed(1).replace('.', ',')}%
    </s.Pill>
  );
};

export default DeltaPill;
